import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Smartphone, MoreHorizontal, Loader2, Wifi, WifiOff } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { FilterBar } from "@/components/shared/FilterBar";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";


interface Channel {
  id: number;
  phone_number_id: string;
  display_phone: string;
  name: string | null;
  verified_name: string | null;
  quality_rating: string;
  messaging_tier: string;
  status: string;
  throughput_level: string;
  waba_name: string;
  created_at: string;
}

function tierLabel(tier: string): string {
  const map: Record<string, string> = {
    TIER_1K: "Tier 1K",
    TIER_10K: "Tier 10K",
    TIER_100K: "Tier 100K",
    TIER_UNLIMITED: "Unlimited",
  };
  return map[tier] || tier;
}

function tierLimit(tier: string): number {
  const map: Record<string, number> = {
    TIER_1K: 1000,
    TIER_10K: 10000,
    TIER_100K: 100000,
    TIER_UNLIMITED: 1000000,
  };
  return map[tier] || 1000;
}

function qualityToStatus(quality: string): string {
  const map: Record<string, string> = {
    GREEN: "high",
    YELLOW: "medium",
    RED: "low",
  };
  return map[quality] || "unknown";
}

export default function Channels() {
  const queryClient = useQueryClient();
  const [signupLoading, setSignupLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["channels"],
    queryFn: () => apiFetch<{ channels: Channel[] }>("/api/channels"),
  });

  const hasRealChannels = data && data.channels.length > 0;
  const displayChannels = hasRealChannels
    ? data.channels.map((ch) => ({
        id: String(ch.id),
        phone: ch.display_phone,
        phoneNumberId: ch.phone_number_id,
        name: ch.name || ch.verified_name || ch.display_phone,
        waba: ch.waba_name || "—",
        quality: qualityToStatus(ch.quality_rating) as "high" | "medium" | "low",
        tier: tierLabel(ch.messaging_tier),
        status: ch.status === "CONNECTED" ? "Active" : ch.status,
        usage: 0,
        limit: tierLimit(ch.messaging_tier),
      }))
    : [];

  const handleEmbeddedSignup = async () => {
    setSignupLoading(true);
    try {
      const { loadFacebookSDK, launchWhatsAppSignup } = await import("@/lib/meta-signup");
      const appId = (document.querySelector('meta[name="meta-app-id"]') as HTMLMetaElement)?.content;
      const configId = (document.querySelector('meta[name="meta-config-id"]') as HTMLMetaElement)?.content;

      if (!appId || !configId) {
        alert("Meta App ID or Config ID not configured. Add meta_app_id and meta_config_id to Rails credentials.");
        return;
      }

      await loadFacebookSDK(appId);
      const result = await launchWhatsAppSignup(configId);

      await apiFetch("/api/channels/embedded_signup", {
        method: "POST",
        body: JSON.stringify({
          code: result.code,
          waba_id: result.waba_id,
          phone_number_id: result.phone_number_id,
        }),
      });

      queryClient.invalidateQueries({ queryKey: ["channels"] });
      setSuccessMessage("WhatsApp Business Account connected successfully!");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      if (err instanceof Error && err.message !== "Signup cancelled or failed") {
        alert(`Signup error: ${err.message}`);
      }
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Channels"
        description="Connected WhatsApp numbers with quality rating and messaging tier."
        actions={
          <Button size="sm" onClick={handleEmbeddedSignup} disabled={signupLoading}>
            {signupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Connect new number
          </Button>
        }
      />

      <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm">
        <span className="font-medium text-primary">Meta Embedded Signup</span>
        <span className="ml-2 text-muted-foreground">— Connect new numbers in under 2 minutes via Meta's secure flow. No phone porting required.</span>
      </div>

      {successMessage && (
        <div className="rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm font-medium text-success">
          {successMessage}
        </div>
      )}

      {!hasRealChannels && !isLoading && (
        <div className="rounded-lg border border-muted bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
          No channels connected yet. Click <strong>"Connect new number"</strong> above to link your WhatsApp Business Account.
        </div>
      )}

      {hasRealChannels && <FilterBar searchPlaceholder="Search by phone or name..." />}

      {hasRealChannels && <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Channel</th>
              <th className="px-4 py-3 text-left font-medium">WABA</th>
              <th className="px-4 py-3 text-left font-medium">Quality</th>
              <th className="px-4 py-3 text-left font-medium">Tier</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Daily usage</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {displayChannels.map((ch) => {
              const pct = ch.limit > 0 ? Math.min(100, Math.round((ch.usage / ch.limit) * 100)) : 0;
              return (
                <tr key={ch.id} className="hover:bg-muted/40">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-success/15 text-success"><Smartphone className="h-4 w-4" /></div>
                      <div>
                        <div className="font-medium">{ch.name}</div>
                        <div className="font-mono text-xs text-muted-foreground">{ch.phone}</div>
                        <div className="font-mono text-[10px] text-muted-foreground/60">ID: {ch.phoneNumberId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{ch.waba}</td>
                  <td className="px-4 py-3"><StatusBadge status={ch.quality} /></td>
                  <td className="px-4 py-3 text-xs">{ch.tier}</td>
                  <td className="px-4 py-3"><StatusBadge status={ch.status} /></td>
                  <td className="w-64 px-4 py-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{ch.usage.toLocaleString()} / {ch.limit.toLocaleString()}</span>
                      <span className="font-medium">{pct}%</span>
                    </div>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div className={`h-full rounded-full ${pct >= 90 ? "bg-destructive" : pct >= 70 ? "bg-warning" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </td>
                  <td className="px-2 py-3"><Button variant="ghost" size="icon" aria-label="Actions"><MoreHorizontal className="h-4 w-4" /></Button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>}

      {hasRealChannels && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="text-sm font-semibold">Connected channels</h3>
            <p className="mt-1 text-3xl font-semibold">{displayChannels.length}</p>
            <p className="text-xs text-muted-foreground">
              {displayChannels.filter(c => c.status === "Active").length} active
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="text-sm font-semibold">Quality overview</h3>
            <dl className="mt-3 space-y-2 text-xs">
              <div className="flex justify-between"><dt className="text-muted-foreground">High quality</dt><dd className="text-success">{displayChannels.filter(c => c.quality === "high").length}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Medium quality</dt><dd className="text-warning">{displayChannels.filter(c => c.quality === "medium").length}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Low quality</dt><dd className="text-destructive">{displayChannels.filter(c => c.quality === "low").length}</dd></div>
            </dl>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="text-sm font-semibold">WABAs</h3>
            <p className="mt-1 text-3xl font-semibold">{new Set(displayChannels.map(c => c.waba)).size}</p>
            <p className="text-xs text-muted-foreground">WhatsApp Business Accounts</p>
          </div>
        </div>
      )}
    </div>
  );
}
